import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Languages } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Chessboard as ReactChessboard } from "react-chessboard";
import { toast } from "sonner";
import { useMutation } from "urql";

import { Button } from "@/components/ui/button";
import { graphql } from "@/gen";
import { AddAnalysisInput } from "@/gen/graphql";
import { GamePlyData, Ply } from "@/gen/realtime";
import { GameReport } from "@/hooks/useRealtimeGame";
import { useViewerContext } from "@/hooks/useViewerContext";
import ChessboardAnalysisInput from "@/routes/game-feed/components/ChessboardAnalysisInput";
import { addFeedRecordMutation, useAddAnalysis } from "@/routes/game-feed/query";
import { generatePositionFen } from "@/utils/chess";
import { useLanguageCodeWithDefault } from "@/utils/language";
import "@/utils/types";

const setFeedRecordAnalysisIdMutation = graphql(`
    mutation setFeedRecordAnalysisId($input: SetFeedRecordAnalysisIdInput!) {
        setFeedRecordAnalysisId(input: $input) {
            id
        }
    }
`);

export const Chessboard = ({
    plyData,
    gameReport,
    plyIndex,
    setPlyIndex,
    disableFollowLive = false,
    enableArrows = false,
    enableAnalysis = false,
}: {
    plyData: GamePlyData;
    gameReport: GameReport | null;
    plyIndex: number;
    setPlyIndex: (index: number) => void;
    disableFollowLive?: boolean;
    enableArrows?: boolean;
    enableAnalysis?: boolean;
}) => {
    const [followLive, setFollowLive] = useState(!disableFollowLive);

    const [latestPlyIndexSeen, setLatestPlyIndexSeen] = useState(plyIndex);
    const setCurrentPlyIndex = useCallback(
        (plyIndex: number) => {
            if (plyIndex > latestPlyIndexSeen) {
                setLatestPlyIndexSeen(plyIndex);
            }
            setPlyIndex(plyIndex);
        },
        [latestPlyIndexSeen, setPlyIndex]
    );

    const latestPlyIndex = plyData.plies.length - 1;
    useEffect(() => {
        const hasNewMoves = latestPlyIndex > latestPlyIndexSeen;

        if (hasNewMoves && followLive) {
            setCurrentPlyIndex(latestPlyIndex);
        }
    }, [followLive, latestPlyIndex, latestPlyIndexSeen, setCurrentPlyIndex]);

    const [{ error: _errorAddAnalysis }, addAnalysis] = useAddAnalysis();
    const [{ error: _errorAddRecord }, addFeedRecord] = useMutation(addFeedRecordMutation);
    const [{ error: _errorupdateFeedRecordAnalysisId }, updateFeedRecordAnalysisId] = useMutation(
        setFeedRecordAnalysisIdMutation
    );
    const languageCode = useLanguageCodeWithDefault();
    const { viewer } = useViewerContext();

    const getAnalyses = () => {
        return (gameReport?.feedRecords ?? [])
            .filter(el => el.analysis != null && el.ply != null)
            .map(item => ({
                plyIndex: item.ply!.plyIndex,
                analyses: {
                    startingPositionFen: item.analysis!.startingPositionFen,
                    annotatedPlies: item.analysis!.annotatedPlies.map(ply => ({
                        plyIndex: ply.ply.plyIndex,
                        san: ply.ply.san,
                        arrows: ply.arrows,
                        highlightedSquares: ply.highlighted_squares,
                        pieceEmojis: ply.piece_emojis,
                    })),
                },
            }));
    };
    const [analyses, setAnalyses] =
        useState<{ plyIndex: number; analyses: AddAnalysisInput }[]>(getAnalyses());
    const updateAnalysis = (plyIndex: number, currAnalysis: AddAnalysisInput) => {
        setAnalyses(prevAnalysis => {
            const existingIndex = prevAnalysis.findIndex(item => item.plyIndex === plyIndex);

            if (existingIndex !== -1) {
                return prevAnalysis.map((item, index) =>
                    index === existingIndex ? { ...item, analyses: currAnalysis } : item
                );
            } else {
                return [...prevAnalysis, { plyIndex, analyses: currAnalysis }];
            }
        });
    };

    const getAnalysisForPly = useMemo((): AddAnalysisInput | undefined => {
        const currAnalysis = analyses.find(el => el.plyIndex === plyIndex);
        const currPly = plyData.plies[plyIndex];

        if (currAnalysis) {
            return {
                startingPositionFen: currAnalysis.analyses.startingPositionFen,
                annotatedPlies: currAnalysis.analyses.annotatedPlies.map(ply => ({
                    plyIndex: ply.plyIndex,
                    san: ply.san,
                    arrows: ply.arrows,
                    highlightedSquares: ply.highlightedSquares,
                    pieceEmojis: ply.pieceEmojis,
                })),
            };
        }
        if (currPly) {
            return {
                startingPositionFen: generatePositionFen({
                    startingPosition: plyData.startingPositionFen,
                    sanPlies: plyData.plies.map(ply => ply.san),
                    plyIndex: plyIndex - 1,
                }),
                annotatedPlies: [
                    {
                        plyIndex: 0,
                        san: currPly.san,
                        arrows: [],
                        highlightedSquares: {},
                        pieceEmojis: {},
                    },
                ],
            };
        }
    }, [analyses, plyData.plies, plyData.startingPositionFen, plyIndex]);

    const createFeedRecordForPly = useCallback(
        async (ply: Ply) => {
            if (!languageCode) {
                toast.error("Unable to create feed record", {
                    description: "Language code missing",
                    icon: <Languages size={18} />,
                });
                return;
            }

            const result = await addFeedRecord({
                input: {
                    gameId: plyData.gameId,
                    languageCode: languageCode as string,
                    draft: false,
                    highlight: false,
                    timestamp: ply.timestamp.toISOString(),
                    commentatorId: viewer?.commentator?.id,
                    plyId: ply.id,
                },
            });
            if (result.error) {
                handleError("Unable to create feed record for ply", result.error.message);

                return;
            }
            return result.data;
        },
        [languageCode, addFeedRecord, plyData.gameId, viewer?.commentator?.id]
    );

    const doSaveAnalysisCall = useCallback(
        async (analysis: AddAnalysisInput, feedRecordId: string) => {
            let analysisId = null;
            const addAnalysisResult = await addAnalysis({
                input: analysis,
            });

            if (addAnalysisResult.error) {
                handleError("Unable to add analysis", addAnalysisResult.error.message);
                return;
            } else {
                toast.success("Analysis for ply is added");
            }

            analysisId = addAnalysisResult.data?.addAnalysis.id ?? null;
            const updateResult = await updateFeedRecordAnalysisId({
                input: {
                    id: feedRecordId,
                    analysisId: analysisId,
                },
            });

            if (updateResult.error) {
                handleError("Unable to update feed record", updateResult.error.message);
                return;
            }
        },
        [addAnalysis, updateFeedRecordAnalysisId]
    );

    const saveAnalysis = useCallback(async () => {
        const analysis = analyses.find(el => el.plyIndex == plyIndex)?.analyses;
        const feedRecord = gameReport?.feedRecords.find(el => el.ply?.plyIndex === plyIndex);

        if (!feedRecord) {
            const ply = plyData.plies[plyIndex];
            if (ply) {
                const createFeedRecordRes = await createFeedRecordForPly(ply);
                const newFeedRecordId = createFeedRecordRes?.addFeedRecord.id;
                if (newFeedRecordId && analysis) {
                    doSaveAnalysisCall(analysis, newFeedRecordId);
                }
            }
        } else if (enableAnalysis && analysis && feedRecord) {
            doSaveAnalysisCall(analysis, feedRecord.id);
        }
    }, [
        analyses,
        createFeedRecordForPly,
        doSaveAnalysisCall,
        enableAnalysis,
        gameReport?.feedRecords,
        plyData.plies,
        plyIndex,
    ]);

    const onGoToStartPress = useCallback(() => {
        setFollowLive(false);
        setPlyIndex(-1);
    }, [setPlyIndex]);

    const onBackPress = useCallback(() => {
        setFollowLive(false);
        if (plyIndex > -1) {
            setPlyIndex(plyIndex - 1);
        }
    }, [plyIndex, setPlyIndex]);

    const onForwardPress = useCallback(() => {
        setFollowLive(false);
        if (plyIndex < plyData.plies.length - 1) {
            setPlyIndex(plyIndex + 1);
        }
    }, [plyData.plies.length, plyIndex, setPlyIndex]);

    const onToggleFollowLive = useCallback(() => {
        setFollowLive(prev => !prev);
    }, []);

    return (
        <div className="flex flex-col gap-8">
            <div className="flex max-w-full justify-center rounded-xl bg-white p-2 px-0 align-middle">
                <div className="w-full max-w-xl">
                    {enableAnalysis && getAnalysisForPly ? (
                        <ChessboardAnalysisInput
                            analysis={getAnalysisForPly}
                            setAnalysis={analyses => updateAnalysis(plyIndex, analyses)}
                        />
                    ) : (
                        <ReactChessboard
                            position={plyData.startingPositionFen}
                            areArrowsAllowed={enableArrows}
                            showBoardNotation
                            arePiecesDraggable={false}
                        />
                    )}
                </div>
            </div>
            <div className="flex flex-col space-y-2">
                {latestPlyIndex > latestPlyIndexSeen && (
                    <div className="text-center text-red-500">
                        New moves are available. Navigate to the latest move to see them.
                    </div>
                )}
                <ChessboardControls
                    plyIndex={plyIndex}
                    sanPliesLength={plyData.plies.length}
                    onGoToStart={() => onGoToStartPress()}
                    onBack={() => onBackPress()}
                    onForward={() => onForwardPress()}
                    onGoToLatest={() => setPlyIndex(plyData.plies.length - 1)}
                    followLive={
                        disableFollowLive
                            ? undefined
                            : { followLive: followLive, onToggleFollowLive: onToggleFollowLive }
                    }
                />
                {enableAnalysis ? (
                    <Button
                        variant="outline"
                        disabled={analyses.find(el => el.plyIndex === plyIndex) == undefined}
                        onClick={() => saveAnalysis()}
                    >
                        Save analysis for ply
                    </Button>
                ) : null}
            </div>
        </div>
    );
};

type ChessboardControlsProps = {
    plyIndex: number;
    sanPliesLength: number;
    onGoToStart: () => void;
    onBack: () => void;
    onForward: () => void;
    onGoToLatest: () => void;
    followLive?: { followLive: boolean; onToggleFollowLive: () => void };
};

const ChessboardControls: React.FC<ChessboardControlsProps> = ({
    plyIndex,
    sanPliesLength,
    onGoToStart,
    onBack,
    onForward,
    onGoToLatest,
    followLive,
}) => {
    return (
        <div className="flex flex-row gap-4">
            <Button variant="outline" onClick={onGoToStart} disabled={plyIndex === -1}>
                First
            </Button>
            <Button variant="outline" onClick={onBack} disabled={plyIndex === -1}>
                <CaretLeft />
            </Button>
            <Button
                variant="outline"
                onClick={onForward}
                disabled={plyIndex === sanPliesLength - 1}
            >
                <CaretRight />
            </Button>
            <Button
                variant="outline"
                onClick={onGoToLatest}
                disabled={plyIndex === sanPliesLength - 1}
            >
                Latest
            </Button>
            {followLive ? (
                <Button variant="outline" onClick={followLive.onToggleFollowLive}>
                    {followLive.followLive ? "Stop Following Live" : "Follow Live"}
                </Button>
            ) : null}
        </div>
    );
};

const handleError = (message: string, errorDetails: string): void => {
    toast.error(message, { description: errorDetails });
};
